import { apiClient } from '@/lib/api/client';
import type { InternalAxiosRequestConfig } from 'axios';
import type { CreateCartItemRequestBody, CartResponse } from '@/features/booking/types/ticketBookingTypes';
import type {
  CartResponse as AllCartsCartResponse,
  CartResponseWithRelatedData,
  CartItemStatusResponse,
  UpdateCartItemRequestBody,
  CheckoutResponse,
  GuestCheckoutRequestBody,
  CartConflictReport,
  InvoiceDownloadResponse
} from '@/features/cart/types';

export const cartService = {
  /**
   * Add an item to the user's cart
   * @deprecated Use addNonTableBookingCartItem for non-table bookings
   */
  addCartItem: async (cartItem: CreateCartItemRequestBody): Promise<CartResponse> => {
    try {
      console.log('Request Body:', JSON.stringify(cartItem, null, 2));

      const response = await apiClient.post<{ data: CartResponse }>(
        '/api/booking/v1/cart/add-cart-item',
        cartItem
      );

      return response.data;
    } catch (error) {
      console.error('Error adding item to cart:', error);
      throw new Error('Failed to add item to cart');
    }
  },

  /**
   * Add a non-table booking cart item to the user's cart
   * Returns the cart ID from the response
   */
  addNonTableBookingCartItem: async (
    eventBookingCategoryID: string,
    cartItem: Omit<CreateCartItemRequestBody, 'event_booking_category_id'>
  ): Promise<{ cartId: string }> => {
    try {
      console.log('Request Body:', JSON.stringify(cartItem, null, 2));

      const response = await apiClient.post<{ data: { cart: { id: string } } }>(
        `/api/booking/v1/cart/event-booking-category/${eventBookingCategoryID}/add-non-table-booking-cart-item`,
        cartItem
      );

      // Response structure from API: { data: { cart: { id: string } } }
      // axios response.data contains the API response body
      // Try both possible response structures
      const cartId = response.data?.cart?.id;

      if (!cartId) {
        console.error('Cart ID not found in response. Full response:', JSON.stringify(response.data, null, 2));
        throw new Error('Cart ID not found in response');
      }

      return { cartId };
    } catch (error) {
      console.error('Error adding non-table booking item to cart:', error);
      // Re-throw the original error if it's already an Error instance
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to add item to cart');
    }
  },

  /**
   * Remove an item from the user's cart
   */
  removeItem: async (id: string): Promise<void> => {
    try {
      await apiClient.delete(`/api/booking/v1/cart/items/${id}`);
    } catch (error) {
      console.error('Error removing item from cart:', error);
      throw new Error('Failed to remove item from cart');
    }
  },

  /**
   * Get all items in the user's cart
   */
  getCartItems: async (): Promise<CartResponse[]> => {
    try {
      const response = await apiClient.get<{ data: CartResponse[] }>('/api/booking/v1/cart/items');
      return response.data;
    } catch (error) {
      console.error('Error fetching cart items:', error);
      throw new Error('Failed to fetch cart items');
    }
  },

  /**
   * Get all incomplete carts for the authenticated user (active, pending_payment, failed_payment)
   */
  getAllCarts: async (): Promise<AllCartsCartResponse[]> => {
    try {
      const response = await apiClient.get<{ data: Array<{ cart: AllCartsCartResponse }> | null }>('/api/booking/v1/cart/all-incomplete');
      // Handle null response (empty cart) gracefully
      if (!response.data || !Array.isArray(response.data)) {
        return [];
      }
      // Extract cart objects from the nested structure
      return response.data.map(item => item.cart);
    } catch (error) {
      console.error('Error fetching all carts:', error);
      throw new Error('Failed to fetch carts');
    }
  },

  /**
   * Get count of incomplete carts for the authenticated user
   * Lightweight endpoint for cart badge display
   */
  getIncompleteCartsCount: async (): Promise<number> => {
    try {
      const response = await apiClient.get<{ data: { count: number } }>(
        '/api/booking/v1/cart/incomplete/count'
      );
      return response.data.count;
    } catch (error) {
      console.error('Error fetching incomplete carts count:', error);
      throw new Error('Failed to fetch cart count');
    }
  },

  /**
   * Get all complete carts for the authenticated user (successful_payment, complete, refunded)
   */
  getAllCompleteCarts: async (): Promise<AllCartsCartResponse[]> => {
    try {
      const response = await apiClient.get<{ data: Array<{ cart: AllCartsCartResponse }> | null }>('/api/booking/v1/cart/all-complete');
      // Handle null response (empty cart) gracefully
      if (!response.data || !Array.isArray(response.data)) {
        return [];
      }
      // Extract cart objects from the nested structure
      return response.data.map(item => item.cart);
    } catch (error) {
      console.error('Error fetching complete carts:', error);
      throw new Error('Failed to fetch complete carts');
    }
  },

  /**
   * Abandon/delete a cart
   */
  abandonCart: async (cartId: string): Promise<void> => {
    try {
      await apiClient.delete(`/api/booking/v1/cart/${cartId}/abandon`);
    } catch (error) {
      console.error('Error abandoning cart:', error);
      throw new Error('Failed to delete cart');
    }
  },

  /**
   * Get a specific cart by its ID
   * Returns cart with related data (add_ons, event_booking_categories)
   */
  getCart: async (cartID: string): Promise<CartResponseWithRelatedData> => {
    try {
      const response = await apiClient.get<{ data: CartResponseWithRelatedData }>(
        `/api/booking/v1/cart/${cartID}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching cart:', error);
      throw new Error('Failed to fetch cart');
    }
  },

  /**
   * Get live status of a cart item
   * Fetches asynchronously to avoid blocking the main cart data load
   */
  getCartItemStatus: async (cartItemID: string): Promise<CartItemStatusResponse> => {
    try {
      const response = await apiClient.get<{ data: CartItemStatusResponse } | CartItemStatusResponse>(
        `/api/booking/v1/cart/cart-item/${cartItemID}/status`
      );


      if ('data' in response && response.data) {
        return response.data;
      }

      if ('status' in response) {
        return response as CartItemStatusResponse;
      }

      throw new Error('Unexpected response structure from getCartItemStatus');
    } catch (error) {
      console.error('Error fetching cart item status:', error);
      throw new Error('Failed to fetch cart item status');
    }
  },

  /**
   * Get Event Booking Category, Add-on Schedules and Related Cart Items together
   * @deprecated This endpoint is no longer needed. The data is now included in getCart response.
   * Use CartResponseWithRelatedData from getCart instead.
   * Keeping for backward compatibility during migration.
   */
  getCartBasedEventBookingCategory: async (
    eventBookingCategoryID: string
  ): Promise<CartResponseWithRelatedData> => {
    try {
      const response = await apiClient.get<{ data: CartResponseWithRelatedData }>(
        `/api/booking/v1/cart/event-booking-category/${eventBookingCategoryID}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching cart-based event booking category:', error);
      throw new Error('Failed to fetch event booking category details');
    }
  },

  /**
   * Update the add-ons & number of guests for a specific cart item
   */
  updateCartItem: async (
    cartItemID: string,
    body: UpdateCartItemRequestBody
  ): Promise<AllCartsCartResponse> => {
    try {
      const response = await apiClient.put<{ data: AllCartsCartResponse }>(
        `/api/booking/v1/cart/cart-item/${cartItemID}`,
        body
      );

      return response.data;
    } catch (error) {
      console.error('Error updating cart item:', error);
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: unknown; status?: number } };
        console.error('Error response data:', axiosError.response?.data);
        console.error('Error status:', axiosError.response?.status);
      }
      throw new Error('Failed to update cart item');
    }
  },

  /**
   * Confirm the price of a cart item after a price change
   */
  confirmCartItemPrice: async (cartItemID: string): Promise<AllCartsCartResponse> => {
    try {
      const response = await apiClient.put<{ data: AllCartsCartResponse }>(
        `/api/booking/v1/cart/cart-item/${cartItemID}/confirm-price`,
        null
      );
      return response.data;
    } catch (error) {
      console.error('Error confirming cart item price:', error);
      throw new Error('Failed to confirm cart item price');
    }
  },

  /**
   * Remove an item from a cart
   */
  removeCartItem: async (cartItemID: string): Promise<AllCartsCartResponse> => {
    try {
      const response = await apiClient.delete<{ data: AllCartsCartResponse }>(
        `/api/booking/v1/cart/cart-item/${cartItemID}`
      );
      return response.data;
    } catch (error) {
      console.error('Error removing cart item:', error);
      throw new Error('Failed to remove cart item');
    }
  },

  /**
   * Creates a payment intent for the specified cart
   * Uses extended timeout (20 seconds) as checkout operations may take longer
   */
  checkoutCart: async (cartID: string): Promise<CheckoutResponse> => {
    try {
      console.log('=== CHECKOUT REQUEST ===');
      console.log('Cart ID:', cartID);
      console.log('Endpoint:', `/api/booking/v1/cart/${cartID}/checkout`);
      console.log('Request body:', null); // Checkout doesn't send body, cart is already saved

      const config: InternalAxiosRequestConfig = { timeout: 20000 } as InternalAxiosRequestConfig;
      const response = await apiClient.post<{ data: CheckoutResponse }>(
        `/api/booking/v1/cart/${cartID}/checkout`,
        null,
        config // 20 seconds timeout for checkout operations
      );
      console.log('Checkout response:', JSON.stringify(response.data, null, 2));
      return response.data;
    } catch (error: unknown) {
      console.error('Error checking out cart:', error);
      // Log the actual error response if available
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: unknown; status?: number } };
        console.error('Error response data:', JSON.stringify(axiosError.response?.data, null, 2));
        console.error('Error status:', axiosError.response?.status);
      }
      // Check if it's a timeout error (Axios timeout errors have code 'ECONNABORTED')
      if (
        error &&
        typeof error === 'object' &&
        (('code' in error && error.code === 'ECONNABORTED') ||
          ('message' in error && typeof error.message === 'string' && error.message.includes('timeout')))
      ) {
        throw new Error('Checkout request timed out. Please try again.');
      }
      throw new Error('Failed to checkout cart');
    }
  },

  /**
   * Creates a payment intent for the specified guest cart
   * Uses extended timeout (20 seconds) as checkout operations may take longer
   */
  checkoutCartAsGuest: async (
    cartID: string,
    body: GuestCheckoutRequestBody
  ): Promise<CheckoutResponse> => {
    try {
      const config: InternalAxiosRequestConfig = { timeout: 20000 } as InternalAxiosRequestConfig;
      const response = await apiClient.post<{ data: CheckoutResponse }>(
        `/api/booking/v1/cart/${cartID}/checkout-guest`,
        body,
        config // 20 seconds timeout for checkout operations
      );
      return response.data;
    } catch (error: unknown) {
      console.error('Error checking out guest cart:', error);
      // Check if it's a timeout error (Axios timeout errors have code 'ECONNABORTED')
      if (
        error &&
        typeof error === 'object' &&
        (('code' in error && error.code === 'ECONNABORTED') ||
          ('message' in error && typeof error.message === 'string' && error.message.includes('timeout')))
      ) {
        throw new Error('Checkout request timed out. Please try again.');
      }
      // Provide user-friendly error message
      throw new Error('Something went wrong. Please try again.');
    }
  },

  /**
   * Cancels a pending or failed checkout, reactivating the cart
   * Uses extended timeout (20 seconds) as cancel checkout operations may take longer
   */
  cancelCheckout: async (cartID: string): Promise<CartResponseWithRelatedData> => {
    try {
      const config: InternalAxiosRequestConfig = { timeout: 20000 } as InternalAxiosRequestConfig;
      const response = await apiClient.put<{ data: CartResponseWithRelatedData }>(
        `/api/booking/v1/cart/${cartID}/cancel-checkout`,
        null,
        config // 20 seconds timeout for cancel checkout operations
      );
      return response.data;
    } catch (error: unknown) {
      console.error('Error canceling checkout:', error);
      // Check if it's a timeout error (Axios timeout errors have code 'ECONNABORTED')
      if (
        error &&
        typeof error === 'object' &&
        (('code' in error && error.code === 'ECONNABORTED') ||
          ('message' in error && typeof error.message === 'string' && error.message.includes('timeout')))
      ) {
        throw new Error('Cancel checkout request timed out. Please try again.');
      }
      throw new Error('Failed to cancel checkout');
    }
  },

  /**
   * Claim guest cart - moves cart ownerships from guest user to real user after signup/login
   * No request body, no response expected
   */
  claimGuestCart: async (): Promise<void> => {
    try {
      await apiClient.put('/api/booking/v1/cart/claim-guest-cart');
    } catch (error) {
      console.error('Error claiming guest cart:', error);
      // Don't throw - this is a best-effort operation that shouldn't fail login/signup
    }
  },

  /**
   * Get incomplete carts for a specific event booking category
   * Returns all incomplete (active, pending_payment, failed_payment) carts for the EBC
   * with items filtered by the EBC (items array contains only items for this EBC, or is empty if none exist)
   */
  getIncompleteCartsForEBC: async (eventBookingCategoryID: string): Promise<AllCartsCartResponse[]> => {
    try {
      const response = await apiClient.get<{ data: Array<{ cart: AllCartsCartResponse }> | null }>(
        `/api/booking/v1/cart/event-booking-category/${eventBookingCategoryID}/incomplete-cart`
      );
      // Handle null response (empty cart) gracefully
      if (!response.data || !Array.isArray(response.data)) {
        return [];
      }
      // Extract cart objects from the nested structure
      return response.data.map(item => item.cart);
    } catch (error) {
      console.error('Error fetching incomplete carts for EBC:', error);
      throw new Error('Failed to fetch incomplete carts');
    }
  },

  /**
   * Get conflict report for a cart
   * Returns availability conflicts and add-on conflicts
   */
  getCartConflictReport: async (cartID: string): Promise<CartConflictReport> => {
    try {
      const response = await apiClient.get<{ data: { conflict_report: CartConflictReport } }>(
        `/api/booking/v1/cart/${cartID}/conflict-report`
      );
      return response.data.conflict_report;
    } catch (error) {
      console.error('Error fetching cart conflict report:', error);
      throw new Error('Failed to fetch cart conflict report');
    }
  },

  /**
   * Get invoice download URL for a completed cart
   * Returns a signed URL for downloading the invoice
   */
  downloadInvoice: async (cartID: string): Promise<InvoiceDownloadResponse> => {
    try {
      const response = await apiClient.post<{ data: InvoiceDownloadResponse }>(
        `/api/booking/v1/cart/${cartID}/invoice`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching invoice download URL:', error);
      throw new Error('Failed to fetch invoice download URL');
    }
  }
}
